<template>
    <div>
        <div v-if="editFeeTable">
            <hr />
            <div class="kgr-row">
                <div class="kgr-col">
                    <kgr-month-picker
                        :month="editFeeTable.start.month"
                        :year="editFeeTable.start.year"
                        :label="$t('kindergarten.feeTable.validSince.mandatory')"
                        :error="error.messageForField('feeTable.start') || feeTableError.messageForField('feeTable.start')"
                        inline
                        @change="onMonthChange"
                    />
                    <kgr-button
                        v-if="feeTableHistory.id != null"
                        :value="$t('kindergarten.feeTable.create')"
                        :pending="false"
                        icon-add
                        :disabled="hasNewFeeTable()"
                        style="margin-top:1.35em"
                        :title="hasNewFeeTable() ? $t('kindergarten.feeTable.createTableNote') : ''"
                        @click="addFeeTable()"
                    />
                    <kgr-button
                        v-if="feeTableHistory.id != null"
                        icon-add
                        style="margin-top:1.35em;margin-left:1.35em"
                        :disabled="hasNewFeeTable()"
                        :title="hasNewFeeTable() ? $t('kindergarten.feeTable.createTableNote') : ''"
                        @click="duplicateFeeTable(editFeeTable)"
                    >
                        {{ $t('kindergarten.feeTable.duplicate') }}
                    </kgr-button>
                </div>
            </div>
            <kgr-note-box text-key="kindergarten.feeTable.valuesNote" />
            <br />
            <kgr-fee-table-values
                :fee-table="editFeeTable"
                :error="error"
                @post-input="onValuesInput($event)"
            />
            <hr />
            <br />
            <div
                v-if="feeTableHistory.feeTables.length > 1"
                class="kgr-row"
            >
                <h3 class="kgr-headline kgr-headline--h3">
                    {{ $t('kindergarten.feeTable.allFeeTables') }}
                </h3><br />
                <table class="kgr-table kgr-table--block kgr-table--striped">
                    <thead>
                        <tr class="kgr-table__row">
                            <th class="kgr-table__cell kgr-table__cell--headline bordered">
                                {{ $t('kindergarten.feeTable.validSince') }}
                            </th>
                            <th class="kgr-table__cell kgr-table__cell--headline"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(feeTable,index) in feeTableHistory.feeTables"
                            class="kgr-table__row kgr-table"
                            :class="{'kgr-backgroundColor--accent': feeTable.id === editFeeTable.id}"
                        >
                            <td class="kgr-table__cell">
                                <kgr-month-picker
                                    :month="feeTable.start.month"
                                    :year="feeTable.start.year"
                                    disabled
                                    inline
                                    :label="null"
                                />
                            </td>
                            <td class="kgr-table__cell">
                                <kgr-icon-show
                                    :disabled="feeTable.id === editFeeTable.id"
                                    :title="feeTable.id !== editFeeTable.id ? $t('kindergarten.feeTable.showFeeTable') : ''"
                                    @click="switchFeeTable(index)"
                                />
                                <kgr-icon-delete
                                    :disabled="feeTable.id !== null"
                                    :title="feeTable.id !== null ? $t('kindergarten.feeTable.feeTableNotDeletable') : ''"
                                    @click="removeFeeTable(feeTable, index)"
                                />
                                <kgr-icon-info
                                    v-if="feeTable.hasChanged()"
                                    highlight
                                    :title="$t('kindergarten.feeTables.feeTableNotSaved')"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
<script>

import Error from '../../js/model/Error';
import FeeTable from '../../js/dto/FeeTable';
import FeeTableHistory from '../../js/dto/FeeTableHistory';
import FeeTableValues from '../../components/kindergarten/FeeTableValues';
import IconDelete from '../common/icon/IconDelete';
import IconInfo from '../common/icon/IconInfo';
import IconShow from '../common/icon/IconShow';
import KgrButton from '../common/form/button/KgrButton';
import MonthPicker from '../common/form/picker/MonthPicker';
import NoteBox from '../../components/common/NoteBox';

export default {
    components: {
        'kgr-fee-table-values': FeeTableValues,
        'kgr-icon-delete': IconDelete,
        'kgr-icon-info': IconInfo,
        'kgr-icon-show': IconShow,
        'kgr-button': KgrButton,
        'kgr-month-picker': MonthPicker,
        'kgr-note-box': NoteBox,
    },
    props: {
        feeTableHistory: {
            type: FeeTableHistory,
            required: true,
        },
        error: {
            type: Error,
            required: true,
        },
    },
    data() {
        return {
            editFeeTable: null,
            feeTableError: new Error(),
        }
    },
    mounted() {
        this.reset();
        if (this.feeTableHistory.feeTables[0]) {
            this.switchFeeTable(0)
        }
    },
    methods: {
        hasNewFeeTable() { // does not work as a computed value
            return null != this.feeTableHistory.feeTables.find(feeTable => feeTable.id === null);
        },
        onMonthChange(monthOfYear) {
            this.editFeeTable.start = monthOfYear;
            this.emit();
            this.$forceUpdate()
        },
        switchFeeTable(index) {
            this.editFeeTable = this.feeTableHistory.feeTables[index];
            this.$emit('current-fee-table',this.editFeeTable);
        },
        duplicateFeeTable(feeTableSource) {
            let feeTable = FeeTable.clone(feeTableSource);
            feeTable.id = null;
            this.feeTableHistory.feeTables.push(feeTable);
            this.editFeeTable = feeTable;
            this.$notify.success('kindergarten.feeTable.duplicated')
            this.$emit('current-fee-table',this.editFeeTable)
            this.$forceUpdate()
        },
        emit() {
            this.$emit('input', this.feeTableHistory)
            this.$nextTick(() => {
                this.$emit('post-input', this.feeTableHistory);
            });
        },
        onValuesInput(values) {
            this.editFeeTable.values = values;
            this.emit();
        },
        reset() {
            this.editFeeTable = null;
            this.resetFeeTableError();
        },
        resetFeeTableError() {
            this.feeTableError = new Error();
        },
        addFeeTable() {
            let feeTable = FeeTable.init(this.feeTableHistory.kindergartenId);
            this.feeTableHistory.feeTables.push(feeTable);
            this.editFeeTable = feeTable;
            this.$emit('current-fee-table',this.editFeeTable)
            this.$forceUpdate()
        },
        removeFeeTable(el, index) {
            this.feeTableHistory.feeTables.splice(index - this.feeTableHistory.feeTables.length,1);
            this.switchFeeTable(0)
            this.$forceUpdate()
        },
    }
}

</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.kgr-backgroundColor--accent {
    background-color: var(--kgr-color-accent) !important;
}
.kgr-inputGroup--inline {
    margin-bottom: 1px;
}
</style>
